import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, Calendar, Check, X, RefreshCw, Scissors } from "lucide-react";
import ServicesEditor from "@/components/admin/ServicesEditor";
import HoursEditor from "@/components/admin/HoursEditor";
import GalleryManager from "@/components/admin/GalleryManager";
import BarbersEditor from "@/components/admin/BarbersEditor";
import WeekReport from "@/components/admin/WeekReport";
import ReviewsManager from "@/components/admin/ReviewsManager";
import AgendaGrid from "@/components/admin/AgendaGrid";
import QuickBook from "@/components/admin/QuickBook";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const LOGO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_971b0a5e-2a58-4bd4-a5ef-b384a7e4c355/artifacts/qroeuqc4_D09061CF-773C-466E-9659-A1C4CF9EC193.png";

const http = axios.create({ withCredentials: true });

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const fmtDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" });
};

const fmtPhone = (p) => {
  if (p.length === 11) return `(${p.slice(0, 2)}) ${p.slice(2, 7)}-${p.slice(7)}`;
  if (p.length === 10) return `(${p.slice(0, 2)}) ${p.slice(2, 6)}-${p.slice(6)}`;
  return p;
};

const STATUS = {
  pending: { label: "Pendente", cls: "border-gold/60 text-gold" },
  confirmed: { label: "Confirmado", cls: "border-eragreen text-green-400" },
  cancelled: { label: "Cancelado", cls: "border-white/15 text-smoke line-through" },
};

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await http.post(`${API}/auth/login`, { password });
      onLogin();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao entrar.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-5" data-testid="admin-login">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm border border-white/10 bg-surface p-8 md:p-10"
      >
        <img src={LOGO} alt="Estúdio New Era" className="h-16 w-16 rounded-full object-cover ring-1 ring-gold/50 mx-auto" />
        <h1 className="font-bebas text-3xl tracking-widest text-cream text-center mt-5">Painel <span className="text-gold">Admin</span></h1>
        <p className="text-xs text-smoke text-center mt-1 uppercase tracking-[0.25em]">Área do barbeiro</p>
        <label htmlFor="admin-password" className="text-xs uppercase tracking-[0.25em] text-smoke block mt-8 mb-2">Senha</label>
        <div className="relative">
          <Lock className="w-4 h-4 text-gold absolute left-4 top-1/2 -translate-y-1/2" />
          <input
            id="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            data-testid="admin-password-input"
            className="focus-gold w-full bg-ink border border-white/15 text-cream font-manrope pl-11 pr-4 py-3.5 outline-none"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !password}
          data-testid="admin-login-button"
          className="focus-gold mt-6 w-full bg-gold text-ink font-bebas text-xl tracking-widest py-3 hover:bg-golddark transition-colors disabled:opacity-40"
        >
          {loading ? "Entrando..." : "Entrar"}
        </button>
        <Link to="/" className="block text-center text-xs uppercase tracking-[0.25em] text-smoke hover:text-gold transition-colors mt-6" data-testid="admin-back-home">
          Voltar ao site
        </Link>
      </motion.form>
    </div>
  );
}

function Agenda() {
  const [date, setDate] = useState(todayISO());
  const [showAll, setShowAll] = useState(false);
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickSlot, setQuickSlot] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`${API}/admin/appointments`, { params: showAll ? {} : { date } });
      setAppts(data);
    } catch {
      toast.error("Erro ao carregar agenda.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [date, showAll]);

  const setStatus = async (id, status) => {
    try {
      await http.patch(`${API}/admin/appointments/${id}`, { status });
      toast.success(status === "confirmed" ? "Agendamento confirmado." : "Agendamento cancelado.");
      load();
    } catch {
      toast.error("Não foi possível atualizar.");
    }
  };

  const counts = {
    pending: appts.filter((a) => a.status === "pending" && !a.blocked).length,
    confirmed: appts.filter((a) => a.status === "confirmed" && !a.blocked).length,
    cancelled: appts.filter((a) => a.status === "cancelled").length,
  };

  return (
    <div data-testid="admin-agenda-tab">
      <main className="max-w-6xl mx-auto px-5 md:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-bebas text-4xl tracking-wide text-cream">Agenda do dia</h2>
            <p className="text-sm text-smoke mt-1">Confirme ou cancele os horários marcados.</p>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="date"
              value={date}
              onChange={(e) => { setDate(e.target.value); setShowAll(false); }}
              data-testid="admin-date-input"
              className="focus-gold bg-surface border border-white/15 text-cream font-manrope px-4 py-2.5 text-sm outline-none [color-scheme:dark]"
            />
            <button
              onClick={() => setShowAll((v) => !v)}
              data-testid="admin-toggle-all-button"
              className={`focus-gold font-bebas text-lg tracking-widest px-5 py-2 border transition-colors ${showAll ? "bg-gold text-ink border-gold" : "border-white/15 text-smoke hover:border-gold hover:text-gold"}`}
            >
              Próximos
            </button>
            <button onClick={load} data-testid="admin-refresh-button" className="focus-gold p-2.5 border border-white/15 text-smoke hover:text-gold hover:border-gold transition-colors" aria-label="Atualizar">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-8" data-testid="admin-stats">
          {[["Pendentes", counts.pending, "text-gold"], ["Confirmados", counts.confirmed, "text-green-400"], ["Cancelados", counts.cancelled, "text-smoke"]].map(([label, n, cls]) => (
            <div key={label} className="border border-white/10 bg-surface p-4 md:p-6">
              <p className={`font-bebas text-3xl md:text-5xl ${cls}`}>{n}</p>
              <p className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-smoke mt-1">{label}</p>
            </div>
          ))}
        </div>

        {!showAll && (
          <AgendaGrid
            date={date}
            appts={appts}
            onPickDate={(d) => { setDate(d); setShowAll(false); }}
            onSlotClick={(barber, time) => setQuickSlot({ barber, time })}
            onBlockClick={(a) => { if (window.confirm(`Desbloquear ${a.time} de ${a.barber_name}?`)) setStatus(a.id, "cancelled"); }}
          />
        )}

        {quickSlot && (
          <QuickBook
            date={date}
            time={quickSlot.time}
            barber={quickSlot.barber}
            onClose={() => setQuickSlot(null)}
            onDone={load}
          />
        )}

        <div className="mt-8 border-t border-white/10" data-testid="admin-appointments-list">
          {loading ? (
            <p className="py-12 text-center text-sm text-smoke" data-testid="admin-loading">Carregando agenda...</p>
          ) : appts.length === 0 ? (
            <div className="py-16 text-center" data-testid="admin-empty">
              <Scissors className="w-8 h-8 text-gold/40 mx-auto mb-4" />
              <p className="text-sm text-smoke">Nenhum agendamento {showAll ? "futuro" : "neste dia"}.</p>
            </div>
          ) : (
            appts.map((a) => (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                data-testid={`admin-appt-${a.id}`}
                className={`flex flex-wrap items-center gap-4 md:gap-6 border-b border-white/10 py-5 ${a.status === "cancelled" ? "opacity-50" : ""}`}
              >
                <div className="w-16 shrink-0">
                  <p className="font-bebas text-3xl text-gold leading-none">{a.time}</p>
                  <p className="text-[10px] uppercase tracking-widest text-smoke mt-1 capitalize">{fmtDate(a.date)}</p>
                </div>
                <div className="flex-1 min-w-[160px]">
                  <p className="font-bebas text-xl tracking-wide text-cream">{a.name}</p>
                  <p className="text-xs text-smoke mt-0.5">{a.service_name} · R$ {a.price} · com {a.barber_name}</p>
                  <a href={`https://wa.me/55${a.whatsapp}`} target="_blank" rel="noreferrer" className="text-xs text-gold/80 hover:text-gold transition-colors" data-testid={`admin-appt-wa-${a.id}`}>
                    {fmtPhone(a.whatsapp)}
                  </a>
                  {a.reference_id && (
                    <a href={`${BACKEND}/api/appointments/${a.id}/reference`} target="_blank" rel="noreferrer" className="inline-block mt-2" data-testid={`admin-appt-ref-${a.id}`} title="Foto de referência enviada pelo cliente">
                      <img src={`${BACKEND}/api/appointments/${a.id}/reference`} alt="Referência do corte" className="w-14 h-14 object-cover border border-gold/40 hover:scale-105 transition-transform" loading="lazy" />
                    </a>
                  )}
                </div>
                <span className={`text-[10px] uppercase tracking-[0.25em] border px-3 py-1.5 ${STATUS[a.status].cls}`} data-testid={`admin-appt-status-${a.id}`}>
                  {STATUS[a.status].label}
                </span>
                {a.status !== "cancelled" && (
                  <div className="flex items-center gap-2">
                    {a.status === "pending" && (
                      <button
                        onClick={() => setStatus(a.id, "confirmed")}
                        data-testid={`admin-confirm-${a.id}`}
                        className="focus-gold flex items-center gap-1.5 bg-gold text-ink font-bebas tracking-widest px-4 py-2 hover:bg-golddark transition-colors"
                      >
                        <Check className="w-4 h-4" /> Confirmar
                      </button>
                    )}
                    <button
                      onClick={() => setStatus(a.id, "cancelled")}
                      data-testid={`admin-cancel-${a.id}`}
                      className="focus-gold flex items-center gap-1.5 border border-white/15 text-smoke font-bebas tracking-widest px-4 py-2 hover:border-red-500 hover:text-red-400 transition-colors"
                    >
                      <X className="w-4 h-4" /> Cancelar
                    </button>
                  </div>
                )}
              </motion.div>
            ))
          )}
        </div>
      </main>
    </div>
  );
}

const TABS = [
  ["agenda", "Agenda"],
  ["servicos", "Serviços"],
  ["barbeiros", "Barbeiros"],
  ["horarios", "Horários"],
  ["galeria", "Galeria"],
  ["avaliacoes", "Avaliações"],
  ["relatorio", "Relatório"],
];

function Shell({ onLogout }) {
  const [tab, setTab] = useState("agenda");
  const logout = async () => {
    try { await http.post(`${API}/auth/logout`); } catch {}
    onLogout();
  };
  return (
    <div className="min-h-screen bg-ink" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-surface sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Estúdio New Era" className="h-9 w-9 rounded-full object-cover ring-1 ring-gold/50" />
            <span className="font-bebas text-lg tracking-widest text-cream">Painel <span className="text-gold">New Era</span></span>
          </div>
          <button onClick={logout} data-testid="admin-logout-button" className="focus-gold flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-smoke hover:text-gold transition-colors">
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-5 md:px-8 pt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
        {TABS.map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            data-testid={`admin-tab-${id}`}
            className={`focus-gold font-bebas text-lg tracking-widest px-6 py-2 border transition-colors ${tab === id ? "bg-gold text-ink border-gold" : "border-white/15 text-smoke hover:border-gold hover:text-gold"}`}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "agenda" && <Agenda />}
      {tab === "servicos" && <ServicesEditor />}
      {tab === "barbeiros" && <BarbersEditor />}
      {tab === "horarios" && <HoursEditor />}
      {tab === "galeria" && <GalleryManager />}
      {tab === "avaliacoes" && <ReviewsManager />}
      {tab === "relatorio" && <WeekReport />}
    </div>
  );
}

export default function Admin() {
  const [auth, setAuth] = useState(null);

  useEffect(() => {
    http.get(`${API}/auth/me`)
      .then(() => setAuth(true))
      .catch(() => setAuth(false));
  }, []);

  if (auth === null) {
    return <div className="min-h-screen bg-ink flex items-center justify-center" data-testid="admin-loading-screen"><p className="text-smoke text-sm">Carregando...</p></div>;
  }
  return auth ? <Shell onLogout={() => setAuth(false)} /> : <Login onLogin={() => setAuth(true)} />;
}
